import * as styledComponents from "styled-components"
import { ThemedStyledProps } from "../styled"
import { Breakpoint } from "./breakpoints"
import { up } from "./mediaQueries"
import { inverseProps, renameKeysWith } from "../utils"
import { ResponsiveProp, isResponsive } from "./utils"
import { curry, map, merge, pick, pipe } from "ramda"

type TLength = string | number
type BreakpointProps<T = TLength> = { xs: ResponsiveProp<T> } & {
  [K in Exclude<Breakpoint, "xs">]?: ResponsiveProp<T>
}

const allBreakpoints = ({
  xs,
  sm = xs,
  md = sm,
  lg = md,
  xl = lg
}: ResponsiveProp) => ({
  xs,
  sm,
  md,
  lg,
  xl
})

const internalProps = ["children", "theme", "forwardedRef", "forwardComponent"]
const isInternalProp = (s: string) => internalProps.includes(s)

const splitResponsiveProps = (
  props: ThemedStyledProps<Record<string, any>>
): [styledComponents.CSSObject, BreakpointProps] =>
  Object.entries(props).reduce(
    ([vanillaProps, mediaProps], [k, v]) =>
      !isInternalProp(k) && isResponsive(v)
        ? [
            vanillaProps,
            {
              ...mediaProps,
              [k]: v
            }
          ]
        : [
            {
              ...vanillaProps,
              [k]: v
            },
            mediaProps
          ],
    [({} as any) as styledComponents.CSSObject, ({} as any) as BreakpointProps]
  )

const includedBreakpoints = (props: BreakpointProps): Breakpoint[] =>
  Array.from(
    new Set(
      Object.values(props).reduce(
        (acc: Breakpoint[], bps) => [
          ...acc,
          ...(Object.keys(bps as {}) as Breakpoint[])
        ],
        []
      )
    )
  )

const expandResponsiveProp = (props: BreakpointProps) =>
  Object.entries(props).reduce(
    (acc, [k, v]) => ({
      ...acc,
      // TODO: Should we pick bps here instead?
      [k]: allBreakpoints(v as ResponsiveProp)
    }),
    {}
  )

const breakpointTransform = curry((bps: Breakpoint[], props: BreakpointProps) =>
  pipe(expandResponsiveProp, inverseProps, pick(bps), merge({ xs: {} }))(props)
)

const breakpointProps = <P>(props: ThemedStyledProps<P>) => {
  const [vanillaProps, breakpointProps]: [
    styledComponents.CSSObject,
    BreakpointProps
  ] = splitResponsiveProps(props)
  const bps = includedBreakpoints(breakpointProps)
  const bProps = breakpointTransform(bps, breakpointProps)
  // FIXME: Fix typing issues
  // @ts-ignore
  return map(merge(vanillaProps), bProps as {}) as ResponsiveProp<P>
}

const responsiveStyles = curry(
  <P>(
    interpolations: (
      | styledComponents.CSSObject
      | styledComponents.Interpolation<ThemedStyledProps<P>>
    )[],
    props: ThemedStyledProps<P>
  ) => {
    const createStyles = (props: ThemedStyledProps<P>) =>
      interpolations.reduce(
        (acc: {}, x) => ({
          ...acc,
          ...(typeof x === "function" ? (x(props) as {}) : x)
        }),
        {}
      )
    const bProps = breakpointProps(props)
    return Object.entries(bProps).reduce(
      (acc, [k, v]) => ({
        ...acc,
        [k]: createStyles(v as ThemedStyledProps<P>)
      }),
      {}
    ) as ResponsiveProp<styledComponents.CSSObject>
  }
)

const propsToMediaQueryStyles = curry((interpolations: any[], props: any) => {
  // FIXME: Fix typing issues
  // @ts-ignore
  const { xs: baseStyles, ...breakpointStyles } = responsiveStyles(
    interpolations,
    props
  )
  return Object.assign(
    baseStyles,
    // FIXME: Fix typing issues
    // @ts-ignore
    renameKeysWith((k: Breakpoint) => up(k, props), breakpointStyles)
  )
})

export {
  breakpointProps,
  breakpointTransform,
  expandResponsiveProp,
  includedBreakpoints,
  propsToMediaQueryStyles,
  responsiveStyles,
  splitResponsiveProps
}
